import { sql } from 'drizzle-orm'
import { z } from 'zod'
import { getD1 } from './d1-context'
import {
  PUBLIC_WORK_SQL,
  publicWorkSchema,
  publicWorkView,
} from './commons/public-work'
import type { PublicWork, PublicWorkRow } from './commons/public-work'
import { readBoundedJson } from '@/lib/bounded-json'
import { publicWorkDetailSchema } from '@/lib/public-work-detail'
import { publicWorkListSchema } from '@/lib/public-work-list'

export async function ssrPublicWorkList() {
  if (import.meta.env.SSR) {
    const db = getD1()
    if (db) return (await import('./commons/public-work-list')).readPublicWorkList(db.$client)
  }
  if (typeof window === 'undefined') throw new Error('Public work storage unavailable')
  const response = await fetch('/api/agents/work?workspace=research&status=open', {
    credentials: 'omit', signal: AbortSignal.timeout(20_000),
  })
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Public work directory unavailable')
  return publicWorkListSchema.parse(await readBoundedJson(response, 256_000))
}

export async function ssrPublicWorkDetail(id: string) {
  if (!z.string().uuid().safeParse(id).success) return null
  if (import.meta.env.SSR) {
    const db = getD1()
    if (db)
      return (
        await import('./commons/public-work-detail')
      ).readPublicWorkDetail(db.$client, id)
  }
  if (typeof window === 'undefined')
    throw new Error('Public work storage unavailable')
  const response = await fetch(`/api/agents/work/${id}`, {
    credentials: 'omit',
    signal: AbortSignal.timeout(20000),
  })
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Public collaboration unavailable')
  const detail = publicWorkDetailSchema.parse(
    await readBoundedJson(response, 6_000_000),
  )
  if (detail.item.id !== id) throw new Error('Unexpected public task')
  return detail
}

export async function ssrPublicWork(id: string): Promise<PublicWork | null> {
  if (!z.string().uuid().safeParse(id).success) return null
  const db = getD1()
  if (db) {
    const row = await db.get<PublicWorkRow | undefined>(
      sql`${sql.raw(PUBLIC_WORK_SQL)}${id}`,
    )
    return row ? publicWorkView(row) : null
  }
  if (typeof window === 'undefined')
    throw new Error('Public work storage unavailable')
  const response = await fetch(
    `/api/agents/work/${encodeURIComponent(id)}/public`,
    { credentials: 'omit' },
  )
  if (response.status === 404) return null
  if (!response.ok) throw new Error('Public work unavailable')
  return publicWorkSchema.parse(await readBoundedJson(response, 64000))
}
